import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Home, ExternalLink, DollarSign, CheckCircle, Star } from "lucide-react";
import ServiceModal from "@/components/ServiceModal";
import { servicesData } from "@/config/servicesData";

const ProfessionalServicesNew = ({ user }) => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);

  const openServiceModal = (service) => {
    setSelectedService(service);
  };

  const closeServiceModal = () => {
    setSelectedService(null);
  };

  const getBadgeColor = (badge) => {
    const colors = {
      'DFW': 'bg-blue-100 text-blue-800',
      'LUXURY': 'bg-purple-100 text-purple-800',
      'VIRTUAL': 'bg-green-100 text-green-800',
      'NATIONAL': 'bg-gray-100 text-gray-800',
      'DIY': 'bg-orange-100 text-orange-800',
      'RECOMMENDED': 'bg-yellow-100 text-yellow-800',
      'TEXAS': 'bg-red-100 text-red-800',
      'REQUIRED': 'bg-pink-100 text-pink-800'
    };
    return colors[badge] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={() => navigate('/search')} variant="ghost">Browse Listings</Button>
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className="bg-orange-600 hover:bg-orange-700">
                  Dashboard
                </Button>
              ) : (
                <Button onClick={() => navigate('/login')} className="bg-orange-600 hover:bg-orange-700">
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Professional Home Services
          </h1>
          <p className="text-xl text-gray-700 mb-3">
            Trusted Partners to Help You Sell Successfully
          </p>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            These are services real estate agents typically coordinate for their clients. 
            We've partnered with the best providers to give you professional support.
          </p>
        </div>

        {/* Value Banner */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 mb-12 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              💰 Save Thousands, Get Professional Results
            </h2>
            <p className="text-lg mb-4">
              You're saving $15,000+ by going FSBO - invest a small portion in these professional services 
              to maximize your sale price and reduce stress.
            </p>
          </div>
        </div>

        {/* Services by Category */}
        <div className="space-y-12">
          {Object.entries(servicesData).map(([key, category]) => (
            <div key={key}>
              <div className="flex items-center space-x-3 mb-6">
                <span className="text-4xl">{category.icon}</span>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>
                    {category.title}
                  </h2>
                  <p className="text-gray-600">{category.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.services.map((service, idx) => (
                  <Card key={idx} className="p-6 hover:shadow-xl transition-shadow border-2 hover:border-orange-300">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">{service.icon}</span>
                        <h3 className="font-bold text-gray-900">{service.name}</h3>
                      </div>
                      {service.badge && (
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getBadgeColor(service.badge)}`}>
                          {service.badge}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 mb-3">{service.tagline}</p>

                    {service.benefits && (
                      <ul className="text-xs text-gray-700 space-y-1 mb-4">
                        {service.benefits.slice(0, 3).map((benefit, i) => (
                          <li key={i} className="flex items-start">
                            <CheckCircle className="w-3 h-3 text-green-600 mr-1 mt-0.5 flex-shrink-0" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {service.price && (
                      <div className="mb-4">
                        <div className="flex items-center space-x-2">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <span className="font-semibold text-green-700">{service.price}</span>
                        </div>
                      </div>
                    )}

                    {service.specialOffer && (
                      <div className="mb-4 p-2 bg-orange-50 rounded text-xs text-orange-800 flex items-start">
                        <Star className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                        <span>{service.specialOffer}</span>
                      </div>
                    )}

                    <Button
                      onClick={() => openServiceModal(service)}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      Learn More <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 bg-gradient-to-br from-slate-50 to-orange-50 rounded-2xl p-8 border-2 border-orange-200">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Ready to List Your Home?
            </h2>
            <p className="text-gray-700 mb-6">
              You now have access to all the professional services agents use. 
              Start your FSBO journey with confidence!
            </p>
            <div className="flex items-center justify-center space-x-4">
              <Button
                onClick={() => navigate(user ? '/create' : '/register')}
                className="bg-purple-600 hover:bg-purple-700 text-lg px-8 py-6"
              >
                {user ? 'Create Listing' : 'Get Started Free'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Service Modal */}
      {selectedService && (
        <ServiceModal service={selectedService} onClose={closeServiceModal} />
      )}
    </div>
  );
};

export default ProfessionalServicesNew;
